import React, { useRef, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { BackgroundTreatment, SeamBridge } from '../components/shared/BackgroundTreatment';
import narrativeImage from '../image/logan-voss-HcdjTHyDrG4-unsplash.jpg';
import { useI18n } from '../i18n';

// ========================
// CONFIGURATION
// ========================
const CONFIG = {
  maxWidth: '720px',
  textColor: 'rgba(255, 255, 255, 0.78)',
};

// ========================
// COMPONENT
// ========================
export const NarrativeChapter: React.FC = () => {
  const { t } = useI18n();
  const sectionRef = useRef<HTMLDivElement>(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Scroll progress: 0 at start of section, 1 at end
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  // ========================
  // KNOWN GOOD TRANSFORMS (piecewise, single useTransform per slide)
  // ========================

  // Slide 1: visible 0-35%, fade out 35-55%
  const s1Opacity = useTransform(scrollYProgress, [0, 0.35, 0.55], [1, 1, 0]);
  const s1Y = useTransform(
    scrollYProgress,
    [0, 0.35, 0.55],
    prefersReducedMotion ? [0, 0, 0] : [0, 0, -10]
  );
  const s1Blur = useTransform(
    scrollYProgress,
    [0, 0.35, 0.55],
    prefersReducedMotion ? [0, 0, 0] : [0, 0, 3]
  );

  // Slide 2: fade in 40-60%, visible 60-100%
  const s2Opacity = useTransform(scrollYProgress, [0.40, 0.60, 1.0], [0, 1, 1]);
  const s2Y = useTransform(
    scrollYProgress,
    [0.40, 0.60, 1.0],
    prefersReducedMotion ? [0, 0, 0] : [10, 0, 0]
  );
  const s2Blur = useTransform(
    scrollYProgress,
    [0.40, 0.60, 1.0],
    prefersReducedMotion ? [0, 0, 0] : [3, 0, 0]
  );

  // Convert blur values to filter strings
  const s1FilterBlur = useTransform(s1Blur, (v) => `blur(${v}px)`);
  const s2FilterBlur = useTransform(s2Blur, (v) => `blur(${v}px)`);

  return (
    <section
      ref={sectionRef}
      id="narrative"
      className="relative w-full"
      style={{ height: '180vh', zIndex: 1 }}
    >
      {/* Sticky Stage - 100vh centered */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Unified Background Treatment - Same as Hero */}
        <BackgroundTreatment
          imageSrc={narrativeImage}
          imageAlt="Narrative background"
          grade="narrative"
          objectPosition="center 30%"
          enableKenBurns={true}
        />

        {/* Seam Bridge - Top fade for smooth transition from Hero */}
        <SeamBridge position="top" height={150} />

        {/* ========================
            Z-20: Text Slides (above overlays)
            ======================== */}
        <div className="relative z-20 h-full grid place-items-center px-6">
          <div
            className="relative w-[92vw] md:w-full translate-y-4"
            style={{
              maxWidth: CONFIG.maxWidth,
              minHeight: '140px',
            }}
          >
            {/* Slide 1 */}
            <motion.p
              className="absolute inset-0 flex items-center justify-center text-center leading-[1.7] font-light whitespace-pre-line"
              style={{
                opacity: s1Opacity,
                y: s1Y,
                filter: s1FilterBlur,
                color: CONFIG.textColor,
                fontSize: 'clamp(16px, 2.5vw, 22px)',
                letterSpacing: '-0.01em',
                textShadow: '0 2px 20px rgba(0,0,0,0.5)',
              }}
            >
              {t.narrative.slide1}
            </motion.p>

            {/* Slide 2 */}
            <motion.p
              className="absolute inset-0 flex items-center justify-center text-center leading-[1.7] font-light whitespace-pre-line"
              style={{
                opacity: s2Opacity,
                y: s2Y,
                filter: s2FilterBlur,
                color: CONFIG.textColor,
                fontSize: 'clamp(16px, 2.5vw, 22px)',
                letterSpacing: '-0.01em',
                textShadow: '0 2px 20px rgba(0,0,0,0.5)',
              }}
            >
              {t.narrative.slide2}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Spacer - garantit que le sticky reste actif pendant tout le scroll */}
      <div aria-hidden="true" style={{ height: '80vh' }} />
    </section>
  );
};

export default NarrativeChapter;
